import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getInvitationDetails, acceptInvitation } from '../../lib/supabase/users';
import {
    FiMail, FiBriefcase, FiUser, FiLock, FiCheckCircle,
    FiAlertCircle, FiArrowRight, FiShield, FiEye, FiEyeOff
} from 'react-icons/fi';

const AcceptInvitation = () => {
    const { invitationId } = useParams();
    const navigate = useNavigate();
    const [invitation, setInvitation] = useState(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    useEffect(() => {
        fetchInvitation();
    }, [invitationId]);

    const fetchInvitation = async () => {

        console.log(invitationId)
        try {
            setLoading(true);
            const data = await getInvitationDetails(invitationId);
            console.log(data)
            setInvitation(data);
        } catch (error) {
            console.error('Error fetching invitation:', error);
            setError(error.message || 'Invalid or expired invitation');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        // Validate passwords
        if (password.length < 8) {
            setError('Password must be at least 8 characters long');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            setSubmitting(true);
            await acceptInvitation(invitationId, password);

            // Success! Redirect to login
            alert('Account created successfully! Please log in.');
            navigate('/login');
        } catch (error) {
            console.error('Error accepting invitation:', error);
            setError(error.message || 'Failed to create account');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-600 border-t-transparent mx-auto mb-4"></div>
                    <p className="text-gray-600 font-medium">Loading invitation...</p>
                </div>
            </div>
        );
    }

    if (error && !invitation) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4">
                <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center border border-gray-100">
                    <div className="w-20 h-20 bg-gradient-to-br from-red-50 to-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <FiAlertCircle className="w-10 h-10 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-3">Invalid Invitation</h2>
                    <p className="text-gray-600 mb-8">{error}</p>
                    <button
                        onClick={() => navigate('/login')}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg hover:shadow-xl font-medium"
                    >
                        Go to Login
                        <FiArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-lg w-full border border-gray-100">
                {/* Notify Logo */}
                <div className="text-center mb-6">
                    <img
                        src="/assets/notify-cropped-wide.png"
                        alt="Notify"
                        className="h-12 mx-auto mb-8"
                    />
                </div>

                {/* Invitation Header with Company Logo */}
                <div className="text-center mb-8">
                    <div className="relative inline-block mb-6">
                        {/* Decorative gradient background */}
                        <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-2xl blur-xl opacity-20"></div>

                        {invitation.companies?.logo_url ? (
                            <div className="relative w-24 h-24 mx-auto bg-white rounded-2xl shadow-lg p-3 border-2 border-gray-100">
                                <img
                                    src={invitation.companies.logo_url}
                                    alt={invitation.companies.name}
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        ) : (
                            <div className="relative w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-2xl flex items-center justify-center shadow-lg border-2 border-gray-100">
                                <FiBriefcase className="w-12 h-12 text-blue-600" />
                            </div>
                        )}
                    </div>

                    <h1 className="text-3xl font-bold text-gray-900 mb-3 bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                        You're Invited!
                    </h1>
                    <p className="text-gray-600 text-lg">
                        Join <strong className="text-gray-900">{invitation.companies?.name}</strong> as a{' '}
                        <span className="inline-flex items-center gap-1 px-3 py-1 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-full text-sm font-semibold text-blue-700 capitalize">
                            <FiShield className="w-3 h-3" />
                            {invitation.role}
                        </span>
                    </p>
                </div>

                {/* Invitation Details */}
                <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-5 mb-8 space-y-3 border border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <FiMail className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Email</p>
                            <p className="font-semibold text-gray-900 truncate">{invitation.email}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm">
                            <FiUser className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Username</p>
                            <p className="font-semibold text-gray-900 truncate">{invitation?.full_name || invitation.username}</p>
                        </div>
                    </div>
                </div>

                {/* Create Password Form */}
                <form onSubmit={handleSubmit} className="space-y-5">
                    <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                            <FiLock className="w-4 h-4 text-blue-600" />
                            Create Password
                        </label>
                        <div className="relative">
                            <input
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none bg-white"
                                placeholder="Enter your password"
                                required
                                minLength={8}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showPassword ? (
                                    <FiEyeOff className="w-5 h-5" />
                                ) : (
                                    <FiEye className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 ml-1 flex items-center gap-1">
                            <FiCheckCircle className="w-3 h-3" />
                            Must be at least 8 characters
                        </p>
                    </div>

                    <div>
                        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
                            <FiLock className="w-4 h-4 text-indigo-600" />
                            Confirm Password
                        </label>
                        <div className="relative">
                            <input
                                type={showConfirmPassword ? "text" : "password"}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all outline-none bg-white"
                                placeholder="Confirm your password"
                                required
                                minLength={8}
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-gray-600 transition-colors"
                            >
                                {showConfirmPassword ? (
                                    <FiEyeOff className="w-5 h-5" />
                                ) : (
                                    <FiEye className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="bg-gradient-to-r from-red-50 to-pink-50 border-2 border-red-200 rounded-xl p-4 flex items-start gap-3">
                            <FiAlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-800 font-medium">{error}</p>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full px-6 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-semibold transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2 group"
                    >
                        {submitting ? (
                            <>
                                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                Creating Account...
                            </>
                        ) : (
                            <>
                                <FiCheckCircle className="w-5 h-5" />
                                Accept Invitation & Create Account
                                <FiArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                            </>
                        )}
                    </button>
                </form>

                <div className="mt-8 pt-6 border-t border-gray-200">
                    <p className="text-xs text-center text-gray-500 flex items-center justify-center gap-2">
                        <FiShield className="w-3 h-3" />
                        By accepting this invitation, you agree to the terms of service
                    </p>
                </div>
            </div>
        </div>
    );
};

export default AcceptInvitation;